import React, { useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { CircularProgress } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-grid.css";

const CallingReports = () => {
  const [rowData, setRowData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const agGridRef = useRef<AgGridReact | null>(null);

  const controller = new AbortController();

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    fetchDataFromAPI(selectedDate);
  }, [selectedDate]);

  const fetchDataFromAPI = async (date: Dayjs) => {
    setIsLoading(true);
    setRowData(null);
    await axiosInterceptorInstance
      .get(`/ThaCrm/voiceCallingReport`, {
        params: { date: date.format("YYYY-MM-DD") },
        signal: controller.signal,
      })
      .then((response: any) => {
        const responseData = response.data.data;
        if (!responseData) {
          const messages: string[] = response.data.errors ?? [
            "An unknown error occurred.",
          ];
          toast.error(messages.join(" "));
          setIsLoading(false);
          return;
        }
        setRowData(responseData);
      })
      .catch((error: any) => {
        console.error(error);
        toast.error("Failed to fetch data. Please try again later.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const exportGridDataAsCSV = () => {
    if (agGridRef.current) {
      const fileName = selectedDate.format("DD-MM-YYYY");

      agGridRef.current.api.exportDataAsCsv({ fileName });
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "DATE",
      headerName: "Date",
      filter: true,
      resizable: true,
      valueFormatter: (p: any) =>
        p.value ? dayjs(p.value).format("DD-MM-YYYY") : "-",
    },
    {
      field: "Origination",
      headerName: "Origination",
      filter: true,
      resizable: true,
    },
    {
      field: "Destination",
      headerName: "Destination",
      filter: true,
      resizable: true,
    },
    {
      field: "TotalCalls",
      headerName: "Total Calls",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Revenue",
      headerName: "Revenue",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (p: any) => (p.value != null ? p.value.toFixed(6) : "-"),
    },
    {
      field: "mobile_rate",
      headerName: "Mobile Rate",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (p: any) => (p.value != null ? p.value.toFixed(3) : "-"),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (p: any) => (p.value != null ? p.value.toFixed(6) : "-"),
    },
    {
      field: "Profit",
      headerName: "Profit",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (p: any) => (p.value != null ? p.value.toFixed(6) : "-"),
    },
    {
      field: "MarginPercent",
      headerName: "Margin Percent",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (p: any) =>
        p.value != null ? p.value.toFixed(4) + "%" : "-",
    },
    {
      field: "Duration",
      headerName: "Duration",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (p: any) => (p.value != null ? p.value.toFixed(6) : "-"),
    },
  ];

  const onGridReady = (params: { api: { sizeColumnsToFit: () => void } }) => {
    params.api.sizeColumnsToFit();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div>
        <h2 className="heading">Calling Report</h2>
      </div>

      <div style={{ margin: "16px 0" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disableFuture
            value={selectedDate}
            onChange={(date) => {
              if (date && date.isValid()) {
                setSelectedDate(date);
              }
            }}
            format="DD-MM-YYYY"
            disabled={isLoading}
          />
        </LocalizationProvider>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <button
          className="small-btn"
          style={{ visibility: rowData ? "visible" : "hidden" }}
          onClick={() => {
            if (rowData) {
              exportGridDataAsCSV();
            }
          }}
        >
          Export as CSV
        </button>
      </div>

      {isLoading && (
        <div className="dummy-conatiner">
          <CircularProgress size={40} />
        </div>
      )}
      {rowData && (
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={agGridRef}
            paginationPageSize={12}
            pagination={true}
            domLayout="autoHeight"
            rowData={rowData}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            enableCellTextSelection={true}
          ></AgGridReact>
        </div>
      )}
    </motion.div>
  );
};

export default CallingReports;
